import { observer } from 'mobx-react';
import { useMemo, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
import { T } from '../../../../../../Translations';
import { learningCollectionsStore } from '../../../../../../stores/LearningCollectionsStore';
import { useLearningCollection } from '../LearningCollectionContext';
import { useSectionTypeOptions } from '../hooks/useSectionTypeOptions';
import BuilderV2HeaderView from './BuilderV2HeaderView';
import BuilderV2SaveActions from './BuilderV2SaveActions';
import Details from './Details';
import Section from './Section';
// DVLP-7601: preview mode swaps the blue stats bar for a light preview bar
// (mode label, license selector, locked-courses toggle, return link).
import PreviewBar from '../../../Preview/AdminPreview/PreviewBar';
import LicenseModal from '../LicenseTracker/LicenseModal';
import { useCategoryTracker } from '../LicenseTracker/useLicenseTracker';
import SectionsDrawer from '../SectionsDrawer';
import { AdaptiveStickyNav, useCollapseOnScroll } from '../../../../../AdaptiveStickyNav';

// State machine: each interface defines only valid transitions for that state
interface ClosedState {
  readonly kind: 'closed';
  openDrawer(): DrawerOpenState;
  openModal(): ModalOpenState;
}

interface DrawerOpenState {
  readonly kind: 'drawer-open';
  closeDrawer(): ClosedState;
  openModal(): DrawerOpenModalOpenState;
}

interface DrawerOpenModalOpenState {
  readonly kind: 'drawer-open-modal-open';
  closeModal(): DrawerOpenState;
}

interface ModalOpenState {
  readonly kind: 'modal-open';
  closeModal(): ClosedState;
}

type DrawerState = ClosedState | DrawerOpenState | DrawerOpenModalOpenState | ModalOpenState;

// Immutable state objects
const closedState: ClosedState = {
  kind: 'closed',
  openDrawer: () => drawerOpenState,
  openModal: () => modalOpenState,
};

const drawerOpenState: DrawerOpenState = {
  kind: 'drawer-open',
  closeDrawer: () => closedState,
  openModal: () => drawerOpenModalOpenState,
};

const drawerOpenModalOpenState: DrawerOpenModalOpenState = {
  kind: 'drawer-open-modal-open',
  closeModal: () => drawerOpenState,
};

const modalOpenState: ModalOpenState = {
  kind: 'modal-open',
  closeModal: () => closedState,
};

interface Props {
  // DVLP-7601: 'preview' reuses the edit-mode layout but with editable={false}
  // throughout, save actions hidden, and a license selector in the blue bar.
  stateKind: 'edit' | 'new' | 'preview';
}

function ReadyBuilder({ stateKind }: Props) {
  const { resourceState } = useLearningCollection();
  // Prefetch section type options for later use when adding sections
  const { sectionTypeOptionsState: _sectionTypeOptionsState } = useSectionTypeOptions();
  const [drawerState, setDrawerState] = useState<DrawerState>(closedState);
  const isNavCollapsed = useCollapseOnScroll();

  // Derived state for components
  const isDrawerOpen =
    drawerState.kind === 'drawer-open' || drawerState.kind === 'drawer-open-modal-open';
  const isLicenseModalOpen =
    drawerState.kind === 'drawer-open-modal-open' || drawerState.kind === 'modal-open';

  const sectionCount = resourceState.payload.product_collections.length;
  const totalPrograms = useMemo(() => {
    return resourceState.payload.product_collections.reduce(
      (total, section) => total + section.payload.experiences.length,
      0,
    );
  }, [resourceState.payload.product_collections]);

  // Get all experience IDs from all sections
  const selectedExperienceIds = useMemo(() => {
    return resourceState.payload.product_collections.flatMap(
      (section) => section.payload.experiences,
    );
  }, [resourceState.payload.product_collections]);

  // Get all experiences from the store
  const storeExperiences =
    learningCollectionsStore.state.kind === 'ready'
      ? learningCollectionsStore.state.data.resource.payload.experiences
      : null;

  const allExperiences = useMemo(() => {
    if (storeExperiences) {
      return storeExperiences;
    }
    return [];
  }, [storeExperiences]);

  const categoryData = useCategoryTracker({
    selectedExperienceIds,
    allExperiences,
  });

  // DISABLED: Success banner logic - now only shown in "Add your first section" modal
  // Show success banner when first section is added (only if has_promotables is true)
  // Only show once per session - don't show again if sections are added/removed later
  /* useEffect(() => {
    const currentSectionCount = resourceState.payload.product_collections.length;
    const prevSectionCount = prevSectionCountRef.current;

    // Only show if:
    // 1. Going from 0 to 1+ sections
    // 2. has_promotables is true
    // 3. Haven't already shown the banner this session
    if (
      prevSectionCount === 0 &&
      currentSectionCount > 0 &&
      resourceState.payload.has_promotables &&
      !hasShownSuccessBannerRef.current
    ) {
      setShowSystemSectionsNotice(true);
      hasShownSuccessBannerRef.current = true; // Mark as shown
      const timer = setTimeout(() => {
        setShowSystemSectionsNotice(false);
      }, 8000);

      return () => clearTimeout(timer);
    }

    // Update the ref for next render
    prevSectionCountRef.current = currentSectionCount;
  }, [resourceState.payload.product_collections.length, resourceState.payload.has_promotables]); */

  return (
    <div className="flex flex-1 flex-col">
      <AdaptiveStickyNav isCollapsed={isNavCollapsed}>
        {/* DVLP-7601: hide the white heading/help bar in preview so the blue
            bar sits at the very top. Name/description already show in Details
            below, and the help button isn't essential for admin preview. */}
        {stateKind !== 'preview' && (
          <BuilderV2HeaderView
            isCollapsed={isNavCollapsed}
            stateKind={stateKind}
            sectionCount={sectionCount}
            totalPrograms={totalPrograms}
            hasUnlicensedPrograms={categoryData.hasUnlicensedPrograms}
            onSectionsButtonClick={() => {
              if (drawerState.kind === 'closed') {
                setDrawerState(drawerState.openDrawer());
              }
            }}
            onLicenseWarningClick={() => {
              if (drawerState.kind === 'closed') {
                setDrawerState(drawerState.openModal());
              } else if (drawerState.kind === 'drawer-open') {
                setDrawerState(drawerState.openModal());
              }
            }}
          >
            <BuilderV2SaveActions stateKind={stateKind} />
          </BuilderV2HeaderView>
        )}

        {stateKind === 'preview' ? (
          <PreviewBar />
        ) : (
          <div
            className="overflow-hidden"
            style={{
              transitionProperty: 'max-height, opacity',
              transitionDuration: '300ms',
              transitionTimingFunction: 'ease-out',
              maxHeight: isNavCollapsed ? '0px' : '80px',
              opacity: isNavCollapsed ? 0 : 1,
            }}
          >
            <div className="bg-primary text-primary-foreground">
              <div className="max-w-clientPortalMaxWidth mx-auto px-6 md:px-14 py-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                      <T kind="Included in this Collection" />
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        if (drawerState.kind === 'closed') {
                          setDrawerState(drawerState.openDrawer());
                        }
                      }}
                      className="flex items-center gap-2 focus:outline-none hover:opacity-80 transition-opacity"
                      data-intercom-target="builder-v2-stats-bar-button"
                    >
                      <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {sectionCount}{' '}
                        {sectionCount === 1 ? <T kind="Section" /> : <T kind="Sections" />}
                        {' • '}
                        {totalPrograms}{' '}
                        {totalPrograms === 1 ? <T kind="Course" /> : <T kind="Courses" />}
                      </span>
                    </button>

                    {categoryData.hasUnlicensedPrograms && (
                      <>
                        <button
                          onClick={() => {
                            if (drawerState.kind === 'closed') {
                              setDrawerState(drawerState.openModal());
                            }
                          }}
                          className="p-1 hover:opacity-80 transition-opacity"
                          aria-label="View license details"
                          data-intercom-target="builder-v2-license-warning-button"
                          data-tooltip-id="license-warning-tooltip"
                          data-tooltip-content="Some programs require additional licensing. Click to view details."
                        >
                          <ExclamationTriangleIcon className="h-5 w-5" />
                        </button>
                        <Tooltip id="license-warning-tooltip" place="bottom" />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </AdaptiveStickyNav>

      <div className="flex flex-1">
        <div className="flex-1">
          <div className="mx-auto box-border px-6 pb-8 pt-11 xl:container md:px-14">
            <Details stateKind={stateKind} />
          </div>

          {/* Success notice for system-generated sections - appears above first section */}
          {/* DISABLED: Success banner now only shown in "Add your first section" modal */}
          {/* {showSystemSectionsNotice && (
            <div className="mx-auto box-border px-6 xl:container md:px-14">
              <div
                className="mb-6 p-4 bg-accent/10 border-l-4 border-accent rounded-md flex items-start gap-3"
                data-success-banner
              >
                <SparklesIcon className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p style={{ fontWeight: 'var(--font-weight-medium)', marginBottom: '4px' }}>
                    We've added 2 system-generated sections for you
                  </p>
                  <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                    "Promoted Courses" and "Personalized for You" have been added to the end of
                    your collection.
                  </p>
                </div>
                <button
                  onClick={() => setShowSystemSectionsNotice(false)}
                  className="p-1 hover:bg-muted rounded transition-colors flex-shrink-0"
                  aria-label="Dismiss notice"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )} */}

          <Section stateKind={stateKind} />
        </div>
      </div>

      {/* Sections & Programs Drawer */}
      <SectionsDrawer
        open={isDrawerOpen}
        onClose={() => {
          if (drawerState.kind === 'drawer-open') {
            setDrawerState(drawerState.closeDrawer());
          }
        }}
        productCollections={resourceState.payload.product_collections}
        hasUnlicensedPrograms={categoryData.hasUnlicensedPrograms}
        onViewLicenseDetails={() => {
          if (drawerState.kind === 'drawer-open') {
            setDrawerState(drawerState.openModal());
          }
        }}
        allExperiences={allExperiences}
      />

      {/* License Modal */}
      <LicenseModal
        isOpen={isLicenseModalOpen}
        onClose={() => {
          if (drawerState.kind === 'drawer-open-modal-open' || drawerState.kind === 'modal-open') {
            setDrawerState(drawerState.closeModal());
          }
        }}
        categoryData={categoryData}
      />
    </div>
  );
}

export default observer(ReadyBuilder);
